import { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { useAuthContext } from '../../features/auth/context/AuthContext';
import { toast } from 'react-hot-toast';
import { TrashIcon, EyeIcon, EyeSlashIcon, PencilIcon, EnvelopeIcon, KeyIcon } from '@heroicons/react/24/outline';
import walletService from '../../features/wallet/services/walletService';

/**
 * PartnerConnectionModal Component
 * A comprehensive modal for connecting with partners - includes sending an email invitation
 * or creating a partner profile manually
 */
const PartnerConnectionModal = ({ isOpen, onClose, onInvitationSent }) => {
  const { 
    user, 
    updatePrivacySettings, 
    createPartnerProfile,
    generatePartnerInvite,
    acceptPartnerInvite,
    refreshUserData
  } = useAuthContext();

  // Modal tabs
  const [activeTab, setActiveTab] = useState('invite'); // 'invite', 'create', or 'accept'
  
  // Partner invitation state
  const [partnerEmail, setPartnerEmail] = useState('');
  
  // Accept invitation state
  const [inviteCode, setInviteCode] = useState('');

  // Partner profile form state
  const [partnerForm, setPartnerForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: ''
  });

  // Privacy settings state
  const [privacySettings, setPrivacySettings] = useState({
    shareIncome: true,
    shareExpenses: true,
    shareSavings: true,
    shareDebt: true
  });

  // Account settings state
  const [accounts, setAccounts] = useState([]);
  const [accountPermissions, setAccountPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [currentStep, setCurrentStep] = useState('connection'); // 'connection', 'privacy', 'accounts'

  // Load user's current privacy settings if they exist
  useEffect(() => {
    if (user?.privacySettings) {
      setPrivacySettings(user.privacySettings);
    }
  }, [user]);

  // Load user accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoadingAccounts(true);
        const response = await walletService.getAccounts();
        setAccounts(response.data || []);
        
        // Initialize account permissions
        const initialPermissions = {};
        response.data.forEach(account => {
          initialPermissions[account._id] = {
            showToPartner: account.visibilitySettings?.showToPartner || false,
            showTransactions: account.visibilitySettings?.showTransactions || false,
            showBalance: account.visibilitySettings?.showBalance || false,
            showInNetWorth: account.visibilitySettings?.showInNetWorth || false,
            accessLevel: 'none' // 'none', 'read', 'write'
          };
        });
        setAccountPermissions(initialPermissions);
      } catch (error) {
        console.error('Failed to fetch accounts:', error);
        toast.error('Could not load your accounts');
      } finally {
        setLoadingAccounts(false);
      }
    };

    if (isOpen) {
      fetchAccounts();
    }
  }, [isOpen]);

  // Handle input change for partner form
  const handlePartnerInputChange = (e) => {
    setPartnerForm({
      ...partnerForm,
      [e.target.name]: e.target.value
    });
  };

  // Check if user has reached the partner limit (1 partner max)
  const hasReachedPartnerLimit = () => {
    // If user already has a partner (either connected or created)
    if (user?.partner || user?.createdPartner) {
      return true;
    }
    
    // We'll let the server handle the invitation limit check
    return false;
  };

  // Handle sending partner invitation
  const handleSendInvitation = async (e) => {
    e.preventDefault();
    
    if (!partnerEmail) {
      toast.error('Please enter your partner\'s email address');
      return;
    }
    
    // Check partner limit client-side first to give immediate feedback
    if (hasReachedPartnerLimit()) {
      toast.error('You have reached the limit of 1 partner. Please disconnect your current partner first.');
      return;
    }
    
    try {
      setLoading(true);
      const response = await generatePartnerInvite(partnerEmail);
      console.log('Partner invite response in modal:', response);
      
      // Check if the response indicates success
      const isSuccess = response && 
                       (response.success || 
                        response.message?.toLowerCase().includes('success') ||
                        response.status === 'success' ||
                        response.status === 200);
      
      if (isSuccess) {
        toast.success('Invitation sent to ' + partnerEmail);
        setPartnerEmail('');
        
        // Notify parent to refresh invitations
        if (onInvitationSent) {
          console.log('🔍 Calling onInvitationSent callback to refresh invitations');
          await onInvitationSent();
        }
        
        // Close modal immediately, the retry mechanism will handle any refetching if needed
        onClose();
      } else {
        // If we have a specific error message, show it
        if (response && response.message) {
          toast.error(response.message);
        } else {
          toast.error('Failed to send invitation');
        }
      }
    } catch (error) {
      console.error('Failed to send invitation:', error);
      
      // Check if the error response indicates the invitation was actually sent
      // Sometimes the rate limiter throws an error but the email is still sent
      if (error.response?.data?.success || 
          error.message?.includes('rate limit') ||
          error.message?.includes('trust proxy')) {
        toast.success('Invitation sent to ' + partnerEmail);
        setPartnerEmail('');
        
        // Notify parent to refresh invitations
        if (onInvitationSent) {
          console.log('🔍 Calling onInvitationSent callback to refresh invitations (error path)');
          await onInvitationSent();
        }
        
        // Close modal immediately, the retry mechanism will handle any refetching if needed
        onClose();
      } else {
        toast.error(error.message || 'Failed to send invitation');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle creating a partner profile manually
  const handleCreatePartner = async (e) => {
    e.preventDefault();
    
    if (!partnerForm.firstName || !partnerForm.lastName) {
      toast.error('Please enter your partner\'s first and last name');
      return;
    }
    
    // Check partner limit client-side first to give immediate feedback
    if (hasReachedPartnerLimit()) {
      toast.error('You have reached the limit of 1 partner. Please disconnect your current partner first.');
      return;
    }
    
    try {
      setLoading(true);
      await createPartnerProfile(partnerForm);
      toast.success('Partner profile created');
      
      // After creating partner, move to privacy settings
      goToNextStep();
    } catch (error) {
      console.error('Failed to create partner profile:', error);
      toast.error(error.message || 'Failed to create partner profile');
    } finally {
      setLoading(false);
    }
  };

  // Handle privacy toggle
  const handlePrivacyToggle = (setting) => {
    setPrivacySettings({
      ...privacySettings,
      [setting]: !privacySettings[setting]
    });
  };

  // Handle account permission change
  const handleAccountPermissionChange = (accountId, setting, value) => {
    setAccountPermissions({
      ...accountPermissions,
      [accountId]: {
        ...accountPermissions[accountId],
        [setting]: value
      }
    });
  };

  // Handle changing account access level
  const handleAccessLevelChange = (accountId, level) => {
    const newPermissions = { ...accountPermissions[accountId] };
    
    // Set permissions based on access level
    if (level === 'none') {
      newPermissions.showToPartner = false;
      newPermissions.showTransactions = false;
      newPermissions.showBalance = false;
      newPermissions.showInNetWorth = false;
    } else if (level === 'read') {
      newPermissions.showToPartner = true;
      newPermissions.showTransactions = true;
      newPermissions.showBalance = true;
      newPermissions.showInNetWorth = true;
    } else if (level === 'write') {
      newPermissions.showToPartner = true;
      newPermissions.showTransactions = true;
      newPermissions.showBalance = true;
      newPermissions.showInNetWorth = true;
      // Additional write permissions would be set here
    }
    
    newPermissions.accessLevel = level;
    
    setAccountPermissions({
      ...accountPermissions,
      [accountId]: newPermissions
    });
  };

  // Navigate to the next step
  const goToNextStep = () => {
    if (currentStep === 'connection') {
      setCurrentStep('privacy');
    } else if (currentStep === 'privacy') {
      setCurrentStep('accounts');
    }
  };

  // Navigate to the previous step
  const goToPreviousStep = () => {
    if (currentStep === 'accounts') {
      setCurrentStep('privacy');
    } else if (currentStep === 'privacy') {
      setCurrentStep('connection');
    }
  };

  // Apply privacy and account settings
  const handleSubmitSettings = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Update privacy settings
      await updatePrivacySettings(privacySettings);
      
      // Update account sharing settings
      for (const [accountId, permissions] of Object.entries(accountPermissions)) {
        try {
          await walletService.updateAccountSharingSettings(accountId, permissions);
        } catch (error) {
          console.error(`Failed to update sharing settings for account ${accountId}:`, error);
        }
      }
      
      toast.success('Partner settings saved successfully!');
      await refreshUserData();
      onClose();
    } catch (error) {
      console.error('Failed to save partner settings:', error);
      toast.error(error.message || 'Failed to save partner settings');
    } finally {
      setLoading(false);
    }
  };

  // Handle accepting a partner invitation
  const handleAcceptInvite = async (e) => {
    e.preventDefault();
    
    if (!inviteCode || inviteCode.trim().length === 0) {
      toast.error('Please enter the invitation code');
      return;
    }
    
    try {
      setLoading(true);
      await acceptPartnerInvite(inviteCode, true); // true means it's a token
      toast.success('Partner invitation accepted successfully!');
      
      // Clear the invitation code input
      setInviteCode('');
      
      // Refresh user data and move to privacy settings
      await refreshUserData();
      goToNextStep();
    } catch (error) {
      console.error('Failed to accept invitation:', error);
      toast.error(error.message || 'Failed to accept invitation. Please check the code and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Render the connection options form (invite, create, or accept)
  const renderConnectionOptions = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900">Connect with Partner</h3>
      
      <div className="mb-2 bg-blue-50 text-blue-800 text-sm p-3 rounded-md border border-blue-200">
        <p className="font-medium">Partner Limit: 1 Maximum</p>
        <p className="text-xs mt-1">You can only have one partner connection at a time. Existing invitations will be cancelled if you create a new connection.</p>
      </div>
      
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('invite')}
            className={`${
              activeTab === 'invite'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
          >
            Send Invitation
          </button>
          
          <button
            onClick={() => setActiveTab('create')}
            className={`${
              activeTab === 'create'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
          >
            Create Profile
          </button>
          
          <button
            onClick={() => setActiveTab('accept')}
            className={`${
              activeTab === 'accept'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
          >
            Accept Invitation
          </button>
        </nav>
      </div>
      
      {activeTab === 'invite' && (
        <div className="mt-4">
          <p className="text-sm text-gray-600 mb-4">
            Send an invitation to your partner's email to connect their account with yours.
          </p>
          
          <form onSubmit={handleSendInvitation} className="space-y-4">
            <div>
              <label htmlFor="partnerEmail" className="block text-sm font-medium text-gray-700">
                Partner's Email Address
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
                <input
                  type="email"
                  name="partnerEmail"
                  id="partnerEmail"
                  value={partnerEmail}
                  onChange={(e) => setPartnerEmail(e.target.value)}
                  className="pl-10 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholder="partner@example.com"
                  required
                />
              </div>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Invitation'}
            </button>
          </form>
        </div>
      )}
      
      {activeTab === 'create' && (
        <div className="mt-4">
          <p className="text-sm text-gray-600 mb-4">
            Create a profile for your partner manually. They won't need to sign up.
          </p>
          
          <form onSubmit={handleCreatePartner} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  id="firstName"
                  value={partnerForm.firstName}
                  onChange={handlePartnerInputChange}
                  className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  id="lastName"
                  value={partnerForm.lastName}
                  onChange={handlePartnerInputChange}
                  className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  required
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email (Optional)
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={partnerForm.email}
                onChange={handlePartnerInputChange}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                  Phone Number (Optional)
                </label>
                <input
                  type="tel"
                  name="phoneNumber"
                  id="phoneNumber"
                  value={partnerForm.phoneNumber}
                  onChange={handlePartnerInputChange}
                  className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">
                  Date of Birth (Optional)
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  id="dateOfBirth"
                  value={partnerForm.dateOfBirth}
                  onChange={handlePartnerInputChange}
                  className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Partner Profile'}
            </button>
          </form>
        </div>
      )}
      
      {activeTab === 'accept' && (
        <div className="mt-4">
          <p className="text-sm text-gray-600 mb-4">
            Enter the invitation code you received from your partner.
          </p>
          
          <form onSubmit={handleAcceptInvite} className="space-y-4">
            <div>
              <label htmlFor="inviteCode" className="block text-sm font-medium text-gray-700">
                Invitation Code
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <KeyIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
                <input
                  type="text"
                  name="inviteCode"
                  id="inviteCode"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  className="pl-10 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md uppercase tracking-wider"
                  placeholder="ABCD12"
                  autoComplete="off"
                  maxLength={6}
                  required
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                The code is 6 characters long and was sent to your email.
              </p>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Accepting...' : 'Accept Invitation'}
            </button>
          </form>
        </div>
      )}
    </div>
  );

  // Render the privacy settings form
  const renderPrivacySettings = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900">Sharing Preferences</h3>
      <p className="text-sm text-gray-500">
        Choose what financial information you want to share with your partner
      </p>
      
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900">Share Income Information</h4>
            <p className="text-xs text-gray-500">Allow your partner to see your income sources and amounts</p>
          </div>
          <button
            type="button"
            onClick={() => handlePrivacyToggle('shareIncome')}
            className={`relative inline-flex h-6 w-11 items-center rounded-full ${
              privacySettings.shareIncome ? 'bg-indigo-600' : 'bg-gray-200'
            }`}
          >
            <span className={`transform transition inline-block h-4 w-4 rounded-full bg-white ${
              privacySettings.shareIncome ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900">Share Expense Information</h4>
            <p className="text-xs text-gray-500">Allow your partner to see your expenses and spending habits</p>
          </div>
          <button
            type="button"
            onClick={() => handlePrivacyToggle('shareExpenses')}
            className={`relative inline-flex h-6 w-11 items-center rounded-full ${
              privacySettings.shareExpenses ? 'bg-indigo-600' : 'bg-gray-200'
            }`}
          >
            <span className={`transform transition inline-block h-4 w-4 rounded-full bg-white ${
              privacySettings.shareExpenses ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900">Share Savings Information</h4>
            <p className="text-xs text-gray-500">Allow your partner to see your savings accounts and goals</p>
          </div>
          <button
            type="button"
            onClick={() => handlePrivacyToggle('shareSavings')}
            className={`relative inline-flex h-6 w-11 items-center rounded-full ${
              privacySettings.shareSavings ? 'bg-indigo-600' : 'bg-gray-200'
            }`}
          >
            <span className={`transform transition inline-block h-4 w-4 rounded-full bg-white ${
              privacySettings.shareSavings ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900">Share Debt Information</h4>
            <p className="text-xs text-gray-500">Allow your partner to see your debts and liabilities</p>
          </div>
          <button
            type="button"
            onClick={() => handlePrivacyToggle('shareDebt')}
            className={`relative inline-flex h-6 w-11 items-center rounded-full ${
              privacySettings.shareDebt ? 'bg-indigo-600' : 'bg-gray-200'
            }`}
          >
            <span className={`transform transition inline-block h-4 w-4 rounded-full bg-white ${
              privacySettings.shareDebt ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
      </div>
    </div>
  );

  // Render the account settings form
  const renderAccountSettings = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900">Account Permissions</h3>
      <p className="text-sm text-gray-500">
        Set individual access levels for each of your accounts
      </p>
      
      {loadingAccounts ? (
        <div className="py-4 text-center">Loading your accounts...</div>
      ) : accounts.length === 0 ? (
        <div className="py-4 text-center text-gray-500">
          You don't have any accounts set up yet.
        </div>
      ) : (
        <div className="space-y-4 max-h-[300px] overflow-y-auto px-1">
          {accounts.map(account => (
            <div key={account._id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium">{account.name}</h4>
                <span className="text-xs px-2 py-1 bg-gray-100 rounded-full">
                  {account.type}
                </span>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Access Level</span>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => handleAccessLevelChange(account._id, 'none')}
                      className={`text-xs px-2 py-1 rounded-md ${
                        accountPermissions[account._id]?.accessLevel === 'none'
                          ? 'bg-gray-700 text-white'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      No Access
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAccessLevelChange(account._id, 'read')}
                      className={`text-xs px-2 py-1 rounded-md ${
                        accountPermissions[account._id]?.accessLevel === 'read'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      View Only
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAccessLevelChange(account._id, 'write')}
                      className={`text-xs px-2 py-1 rounded-md ${
                        accountPermissions[account._id]?.accessLevel === 'write'
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      Full Access
                    </button>
                  </div>
                </div>
                
                {accountPermissions[account._id]?.accessLevel !== 'none' && (
                  <div className="pt-2 space-y-2 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Show Balance</span>
                      <button
                        type="button"
                        onClick={() => 
                          handleAccountPermissionChange(
                            account._id, 
                            'showBalance', 
                            !accountPermissions[account._id].showBalance
                          )
                        }
                        className={`text-xs px-2 py-1 rounded-md ${
                          accountPermissions[account._id]?.showBalance
                            ? 'bg-indigo-100 text-indigo-800'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {accountPermissions[account._id]?.showBalance ? 'Visible' : 'Hidden'}
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Show Transactions</span>
                      <button
                        type="button"
                        onClick={() => 
                          handleAccountPermissionChange(
                            account._id, 
                            'showTransactions', 
                            !accountPermissions[account._id].showTransactions
                          )
                        }
                        className={`text-xs px-2 py-1 rounded-md ${
                          accountPermissions[account._id]?.showTransactions
                            ? 'bg-indigo-100 text-indigo-800'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {accountPermissions[account._id]?.showTransactions ? 'Visible' : 'Hidden'}
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Include in Net Worth</span>
                      <button
                        type="button"
                        onClick={() => 
                          handleAccountPermissionChange(
                            account._id, 
                            'showInNetWorth', 
                            !accountPermissions[account._id].showInNetWorth
                          )
                        }
                        className={`text-xs px-2 py-1 rounded-md ${
                          accountPermissions[account._id]?.showInNetWorth
                            ? 'bg-indigo-100 text-indigo-800'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {accountPermissions[account._id]?.showInNetWorth ? 'Included' : 'Excluded'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render the modal content based on current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'connection':
        return renderConnectionOptions();
      case 'privacy':
        return renderPrivacySettings();
      case 'accounts':
        return renderAccountSettings();
      default:
        return renderConnectionOptions();
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-2xl w-full rounded-xl bg-white p-6 shadow-xl">
          <Dialog.Title className="text-xl font-semibold text-gray-900 mb-4">
            Partner Connection
          </Dialog.Title>
          
          <div className="mb-8">
            {renderCurrentStep()}
          </div>
          
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => currentStep === 'connection' ? onClose() : goToPreviousStep()}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500"
            >
              {currentStep === 'connection' ? 'Cancel' : 'Back'}
            </button>
            
            <button
              type="button"
              onClick={currentStep === 'accounts' ? handleSubmitSettings : goToNextStep}
              disabled={loading}
              className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? 'Processing...' : currentStep === 'accounts' ? 'Finish' : 'Next'}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default PartnerConnectionModal; 